import { useQuery } from '@tanstack/react-query';

import { request } from '~/services/common';

import { canAccessLeLens } from '~/utils/canAccessLeLens';
import isProd from '~/utils/isProd';
import { resolveReportingLeDataUrl } from '~/utils/resolveReportingLeDataUrl';

import useCurrentUser from './useCurrentUser';

export function useLensAccessDecision() {
  const { user } = useCurrentUser();
  const needsLookup = Boolean(isProd() && user?.email && !user.roles?.includes('reporting-admin'));
  const access = useQuery({
    queryKey: ['askRichard', 'pilot-access', user?.email],
    queryFn: async ({ signal }) => {
      const response = (await request(resolveReportingLeDataUrl('/api/reporting/le-data/pilot-access'), {
        signal,
      })) as { allowed: boolean };
      return response.allowed === true;
    },
    enabled: needsLookup,
    staleTime: Infinity,
    gcTime: Infinity,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: false,
    retryOnMount: false,
  });

  return {
    canAccess: canAccessLeLens(user, access.data === true),
    isLoading: needsLookup && access.isPending,
    // A failed refetch retains the last resolved allow/deny decision.
    isError: needsLookup && access.isError && access.data === undefined,
  };
}

export default function useCanAccessLeLens(): boolean {
  return useLensAccessDecision().canAccess;
}
